import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/firebase";
import { createFileRoute } from "@tanstack/react-router";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Inbox, ExternalLink, Clock } from "lucide-react";

export const Route = createFileRoute("/dashboard/worker/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = useAuth();
  const messages = useWorkerMessages(user?.uid);

  return (
    <div className="container mx-auto space-y-6 p-1 sm:p-6">
      <div className="flex items-center gap-3">
        <Inbox className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold">Messages</h1>
          <p className="text-muted-foreground">Your inbox and notifications</p>
        </div>
        {messages.length > 0 && (
          <Badge variant="secondary" className="ml-auto">
            {messages.length} message{messages.length !== 1 ? "s" : ""}
          </Badge>
        )}
      </div>

      <div className="space-y-4">
        {messages.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Inbox className="text-muted-foreground mb-4 h-12 w-12" />
              <h3 className="mb-2 text-lg font-semibold">No messages yet</h3>
              <p className="text-muted-foreground text-center">
                When you receive messages from your managers or administrators, they will appear
                here.
              </p>
            </CardContent>
          </Card>
        ) : (
          messages.map((message) => <MessageCard key={message.id} message={message} />)
        )}
      </div>
    </div>
  );
}

function MessageCard({ message }: { message: WorkerMessage }) {
  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Message</CardTitle>
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4" />
            {message.timestamp ? formatTimestamp(message.timestamp) : "Just now"}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="prose prose-sm max-w-none">
          <MessageContent content={message.message} />
        </div>
      </CardContent>
    </Card>
  );
}

function MessageContent({ content }: { content: string }) {
  const linkifyText = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 underline decoration-2 underline-offset-2 transition-colors hover:text-blue-800 hover:decoration-blue-800"
          >
            {part}
            <ExternalLink className="h-3 w-3" />
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className="leading-relaxed whitespace-pre-wrap text-gray-800">{linkifyText(content)}</div>
  );
}

function formatTimestamp(timestamp: any): string {
  if (!timestamp) return "Unknown time";

  try {
    let date: Date;
    if (timestamp.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      // Assume it's a number (milliseconds)
      date = new Date(timestamp);
    }

    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes <= 0 ? "Just now" : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 24 * 7) {
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  } catch (error) {
    return "Unknown time";
  }
}

function useWorkerMessages(uid: string | undefined | null) {
  const [messages, setMessages] = useState<WorkerMessage[]>([]);

  useEffect(() => {
    if (!uid) return;

    const unsubscribe = onSnapshot(
      query(collection(db, "Workers", uid, "messages"), orderBy("timestamp", "desc")),
      (snapshot) => {
        setMessages(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            message: doc.data().message,
            timestamp: doc.data().timestamp,
          })),
        );
      },
      (error) => {
        console.error("Error fetching messages:", error);
      },
    );

    return () => unsubscribe();
  }, [uid]);

  return messages;
}

interface WorkerMessage {
  id: string;
  message: string;
  timestamp?: any;
}
